import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Switch,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { Semaphore } from './Semaphore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export const CustomReports = () => {
  const [updateConfig, { isLoading: isUpdating }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const ff_customReports = remoteAppConfig?.ff_customReports;
  const ff_web_customReports = remoteAppConfig?.ff_web_customReports;

  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const onSwitch = useCallback(async () => {
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: {
          ff_customReports: ff_customReports ? 0 : 1,
        },
      },
      {
        onSettled,
      },
    );
  }, [updateConfig, onSettled, ff_customReports]);

  const onSwitchWeb = useCallback(async () => {
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: {
          ff_web_customReports: ff_web_customReports ? 0 : 1,
        },
      },
      {
        onSettled,
      },
    );
  }, [updateConfig, onSettled, ff_web_customReports]);

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">Custom Reports (mobile)</Typography>
        <Tooltip title="Mobile / native: when enabled globally, therapists can create custom report prompts and pick them in session note format settings. Can be overridden per therapist in therapist edit.">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Switch
            checked={!!ff_customReports}
            onChange={onSwitch}
            disabled={isUpdating}
          />
        </Box>
        <Box
          marginLeft="1rem"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {isUpdating && <CircularProgress size={14} />}
          {!isUpdating && <Semaphore enabled={!!ff_customReports} />}
        </Box>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
        marginTop="2rem"
      >
        <Typography variant="h6">
          Therapists web — Custom Reports
        </Typography>
        <Tooltip title="When enabled globally, therapists-web shows Custom Reports (custom prompt frameworks). Independent from mobile ff_customReports. Can be overridden per therapist in therapist edit (ff_web_customReports).">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Switch
            checked={!!ff_web_customReports}
            onChange={onSwitchWeb}
            disabled={isUpdating}
          />
        </Box>
        <Box
          marginLeft="1rem"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {isUpdating && <CircularProgress size={14} />}
          {!isUpdating && (
            <Semaphore enabled={!!ff_web_customReports} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
